import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Divider,
  Button,
  IconButton,
  Chip,
  Avatar,
  CardMedia,
  Skeleton,
  useTheme,
  useMediaQuery,
  Paper,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  alpha,
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  Share,
  LocationOn,
  ArrowBack,
  Pets,
  SmokingRooms,
  LocalBar,
  ElectricBolt,
  CalendarToday,
  Close,
  NavigateBefore,
  NavigateNext,
  Report,
  ChatBubbleOutline,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { GetDetail } from '../../services/advert';
import { toggleFavorite } from '../../redux/slices/advertsSlice';
import { toast } from 'react-toastify';
import { formatPrice } from '../../utils/format';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useAuth } from '../../contexts/AuthContext';
import LoginRequiredModal from '../../components/common/LoginRequiredModal';
import VerifiedBadge from '../../components/common/VerifiedBadge';
import { Helmet } from 'react-helmet-async';

const AdvertisementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser, checkAuth } = useAuth();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginAction, setLoginAction] = useState('');

  const isLoggedIn = !!currentUser || checkAuth?.();

  useEffect(() => {
    const fetchListing = async () => {
      try {
        setLoading(true);
        const response = await GetDetail(id);
        if (response?.data) {
          setListing(response.data);
          setIsFavorite(response.data.isFav || false);
        } else {
          setError(t('advertisement.noListing'));
        }
      } catch (err) {
        setError(t('advertisement.error'));
      } finally {
        setLoading(false);
      }
    };
    fetchListing();
  }, [id, t, currentUser]);

  const requireLogin = (action) => {
    setLoginAction(action);
    setLoginOpen(true);
  };

  const handleFavoriteToggle = async () => {
    if (!isLoggedIn) {
      requireLogin(t('advertisement.favorite') || 'Favori');
      return;
    }
    try {
      await dispatch(toggleFavorite({ id, isFav: isFavorite })).unwrap();
      setIsFavorite(!isFavorite);
      toast.success(
        isFavorite
          ? t('advertisement.removedFromFavorites')
          : t('advertisement.addedToFavorites')
      );
    } catch (err) {
      toast.error(t('advertisement.favoriteError'));
    }
  };

  const handleSendMessage = () => {
    if (!isLoggedIn) {
      requireLogin(t('advertisement.sendMessage'));
      return;
    }
    const data = listing?.data || listing;
    const userId = data?.userId;
    if (!userId || userId === '00000000-0000-0000-0000-000000000000') {
      toast.info(t('auth.loginRequired.subtitle'));
      return;
    }
    navigate(`/chat`);
  };

  const handleReport = () => {
    if (!isLoggedIn) {
      requireLogin(t('advertisement.report'));
      return;
    }
    toast.info(t('advertisement.report'));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'd MMMM yyyy', { locale: tr });
    } catch {
      return '';
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        toast.success(t('advertisement.linkCopied'));
        setShareDialogOpen(false);
      })
      .catch(() => toast.error(t('errors.general')));
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={380} sx={{ borderRadius: 3, mb: 2 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton height={220} sx={{ borderRadius: 3 }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton height={220} sx={{ borderRadius: 3 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error || !listing) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h5" color="error" gutterBottom>
            {error || t('advertisement.noListing')}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
            {t('common.back')}
          </Button>
        </Container>
      </Box>
    );
  }

  const listingData = listing?.data || listing;
  const images =
    listingData?.images?.length > 0
      ? listingData.images.map((img) => img.fileName || '/logo.png')
      : ['/logo.png'];

  const locationText = [listingData.districtName, listingData.cityName, listingData.countryName]
    .filter(Boolean)
    .join(', ');

  return (
    <Box
      sx={{
        minHeight: '70vh',
        pb: 6,
        background:
          theme.palette.mode === 'dark'
            ? theme.palette.background.default
            : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${theme.palette.background.default} 280px)`,
      }}
    >
      <Helmet>
        <title>{listingData.listingTitle || 'OdaZen'} | OdaZen</title>
      </Helmet>

      <Container maxWidth="lg" sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <ArrowBack />
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={800} noWrap>
              {listingData?.listingTitle || t('advertisement.noTitle')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.25 }}>
              <LocationOn sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary" noWrap>
                {locationText}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  height={isMobile ? 260 : 420}
                  image={images[photoIndex]}
                  alt={listingData?.listingTitle}
                  sx={{ objectFit: 'cover', cursor: 'pointer' }}
                  onClick={() => setIsLightboxOpen(true)}
                />
                {images.length > 1 && (
                  <>
                    <IconButton
                      sx={{
                        position: 'absolute',
                        left: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: alpha('#fff', 0.92),
                      }}
                      onClick={() =>
                        setPhotoIndex((photoIndex + images.length - 1) % images.length)
                      }
                    >
                      <NavigateBefore />
                    </IconButton>
                    <IconButton
                      sx={{
                        position: 'absolute',
                        right: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: alpha('#fff', 0.92),
                      }}
                      onClick={() => setPhotoIndex((photoIndex + 1) % images.length)}
                    >
                      <NavigateNext />
                    </IconButton>
                  </>
                )}
                <Chip
                  label={`${photoIndex + 1} / ${images.length}`}
                  size="small"
                  sx={{
                    position: 'absolute',
                    bottom: 12,
                    right: 12,
                    bgcolor: alpha('#0F172A', 0.72),
                    color: '#fff',
                    fontWeight: 600,
                  }}
                />
              </Box>

              {images.length > 1 && (
                <Box sx={{ display: 'flex', gap: 1, p: 1.5, overflowX: 'auto' }}>
                  {images.map((image, index) => (
                    <Box
                      key={index}
                      component="img"
                      src={image}
                      alt=""
                      onClick={() => setPhotoIndex(index)}
                      sx={{
                        width: 72,
                        height: 72,
                        objectFit: 'cover',
                        borderRadius: 2,
                        cursor: 'pointer',
                        border:
                          index === photoIndex
                            ? `2px solid ${theme.palette.primary.main}`
                            : `1px solid ${theme.palette.divider}`,
                        opacity: index === photoIndex ? 1 : 0.75,
                      }}
                    />
                  ))}
                </Box>
              )}
            </Paper>

            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: { xs: 2.5, md: 3 },
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                  mb: 2.5,
                  flexWrap: 'wrap',
                }}
              >
                <Box>
                  <Typography variant="h4" fontWeight={800} color="primary" sx={{ fontSize: { xs: '1.6rem', md: '2rem' } }}>
                    {formatPrice(listingData.monthlyRent)} {t('advertisement.currency')}
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      / {t('advertisement.month')}
                    </Typography>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={handleFavoriteToggle}
                    sx={{ border: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' }}
                  >
                    {isFavorite ? <Favorite color="error" /> : <FavoriteBorder />}
                  </IconButton>
                  <IconButton
                    onClick={() => setShareDialogOpen(true)}
                    sx={{ border: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' }}
                  >
                    <Share />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="h6" fontWeight={700} gutterBottom>
                {t('advertisement.detail.features.title')}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
                {listingData.petsAllowed && (
                  <Chip icon={<Pets fontSize="small" />} label={t('advertisement.detail.features.petsAllowed')} variant="outlined" />
                )}
                {listingData.smokingAllowed && (
                  <Chip icon={<SmokingRooms fontSize="small" />} label={t('advertisement.detail.features.smokingAllowed')} variant="outlined" />
                )}
                {listingData.alcolAllowed && (
                  <Chip icon={<LocalBar fontSize="small" />} label={t('advertisement.detail.features.alcoholAllowed')} variant="outlined" />
                )}
                {listingData.utilitiesIncluded && (
                  <Chip icon={<ElectricBolt fontSize="small" />} label={t('advertisement.detail.features.utilitiesIncluded')} variant="outlined" />
                )}
                {listingData.availableFrom && (
                  <Chip icon={<CalendarToday fontSize="small" />} label={formatDate(listingData.availableFrom)} color="primary" variant="outlined" />
                )}
              </Stack>

              <Divider sx={{ my: 2.5 }} />

              <Typography variant="h6" fontWeight={700} gutterBottom>
                {t('advertisement.detail.description')}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'text.secondary', lineHeight: 1.75 }}>
                {listingData.description || '—'}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 96,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 700 }}>
                {t('advertisement.detail.owner.title')}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main',
                    fontWeight: 700,
                  }}
                >
                  {(listingData.name || '?').charAt(0)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={700}>
                    {[listingData.name, listingData.lastName].filter(Boolean).join(' ')}
                  </Typography>
                  {listingData.age && (
                    <Typography variant="body2" color="text.secondary">
                      {listingData.age}
                    </Typography>
                  )}
                  {listingData.isIdentityVerified === true && (
                    <VerifiedBadge verified variant="chip" sx={{ mt: 0.75 }} />
                  )}
                </Box>
              </Box>

              <Button
                variant="contained"
                fullWidth
                startIcon={<ChatBubbleOutline />}
                onClick={handleSendMessage}
                sx={{ mb: 1.25, py: 1.2 }}
              >
                {t('advertisement.sendMessage')}
              </Button>

              <Button
                variant="outlined"
                color="error"
                fullWidth
                startIcon={<Report />}
                onClick={handleReport}
                sx={{ py: 1.1 }}
              >
                {t('advertisement.report')}
              </Button>

              <Divider sx={{ my: 2.5 }} />

              <Stack spacing={1.2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('advertisement.detail.listingInfo.createdDate')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatDate(listingData.createdDate)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('advertisement.detail.listingInfo.status')}
                  </Typography>
                  <Chip label={t('advertisement.status.available')} size="small" color="success" />
                </Box>
              </Stack>

              {!isLoggedIn && (
                <Box
                  sx={{
                    mt: 2.5,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {t('auth.loginRequired.body')}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Dialog open={isLightboxOpen} onClose={() => setIsLightboxOpen(false)} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 0, bgcolor: '#0F172A', position: 'relative' }}>
          <IconButton
            onClick={() => setIsLightboxOpen(false)}
            sx={{ position: 'absolute', top: 12, right: 12, color: '#fff', zIndex: 2 }}
          >
            <Close />
          </IconButton>
          <Box
            component="img"
            src={images[photoIndex]}
            alt=""
            sx={{ width: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block' }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>{t('advertisement.share')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {window.location.href}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>{t('common.cancel') || 'İptal'}</Button>
          <Button variant="contained" onClick={copyToClipboard}>
            {t('advertisement.copyLink') || 'Linki kopyala'}
          </Button>
        </DialogActions>
      </Dialog>

      <LoginRequiredModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        actionLabel={loginAction}
      />
    </Box>
  );
};

export default AdvertisementDetail;
